/* Indian-states map for mega-environment visualization. Uses datamaps
   TopoJSON (public/geo/india-states.json). Each location that matches an
   Indian state name is colored by its mega-environment cluster.
*/
import React, { useEffect, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

const CLUSTER_COLORS = ['#059669', '#DC2626', '#2563EB', '#D97706', '#7C3AED', '#DB2777'];

// Normalize location name to state name in topojson
const STATE_ALIASES = {
  'delhi': 'Delhi',
  'new delhi': 'Delhi',
  'lucknow': 'Uttar Pradesh',
  'kanpur': 'Uttar Pradesh',
  'varanasi': 'Uttar Pradesh',
  'agra': 'Uttar Pradesh',
  'meerut': 'Uttar Pradesh',
  'pune': 'Maharashtra',
  'mumbai': 'Maharashtra',
  'nagpur': 'Maharashtra',
  'nashik': 'Maharashtra',
  'hyderabad': 'Andhra Pradesh',
  'bangalore': 'Karnataka',
  'bengaluru': 'Karnataka',
  'mysore': 'Karnataka',
  'chennai': 'Tamil Nadu',
  'coimbatore': 'Tamil Nadu',
  'kolkata': 'West Bengal',
  'calcutta': 'West Bengal',
  'jaipur': 'Rajasthan',
  'jodhpur': 'Rajasthan',
  'udaipur': 'Rajasthan',
  'bhopal': 'Madhya Pradesh',
  'indore': 'Madhya Pradesh',
  'gwalior': 'Madhya Pradesh',
  'ludhiana': 'Punjab',
  'amritsar': 'Punjab',
  'chandigarh': 'Chandigarh',
  'ahmedabad': 'Gujarat',
  'surat': 'Gujarat',
  'vadodara': 'Gujarat',
  'rajkot': 'Gujarat',
  'kochi': 'Kerala',
  'thiruvananthapuram': 'Kerala',
  'patna': 'Bihar',
  'ranchi': 'Jharkhand',
  'bhubaneswar': 'Orissa',
  'cuttack': 'Orissa',
  'guwahati': 'Assam',
  'shimla': 'Himachal Pradesh',
  'srinagar': 'Jammu and Kashmir',
  'dehradun': 'Uttaranchal',
};

function normalizeState(name) {
  if (!name) return null;
  const k = String(name).trim().toLowerCase();
  // If alias matches
  if (STATE_ALIASES[k]) return STATE_ALIASES[k];
  // If "loc_year" format, extract the location part
  const parts = k.split(/[_\s-]/);
  for (const p of parts) {
    if (STATE_ALIASES[p]) return STATE_ALIASES[p];
  }
  // Try direct title-case match
  const tc = k.replace(/\b\w/g, c => c.toUpperCase());
  return tc;
}

export default function IndiaMegaEnvMap({ clusters }) {
  const [topo, setTopo] = useState(null);

  useEffect(() => {
    fetch('/geo/india-states.json')
      .then(r => r.json())
      .then(setTopo)
      .catch(() => setTopo(null));
  }, []);

  if (!clusters || clusters.length === 0) return null;

  // Build state -> color map
  const stateColorMap = {};
  const matchedStates = new Set();
  clusters.forEach((c, i) => {
    const color = CLUSTER_COLORS[i % CLUSTER_COLORS.length];
    (c.environments || []).forEach(env => {
      const st = normalizeState(env);
      if (st) {
        stateColorMap[st] = { color, cluster: c.label, env, bestGenotype: c.best_genotype };
        matchedStates.add(st);
      }
    });
  });

  if (matchedStates.size === 0) return null;

  return (
    <div className="rounded-xl p-4 bg-white border border-gray-200" data-testid="india-mega-env-map">
      <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">
        Mega-environment Map (India)
      </h4>
      {topo ? (
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{ scale: 900, center: [82, 22] }}
          width={560}
          height={560}
          style={{ maxWidth: '100%', height: 'auto' }}
        >
          <Geographies geography={topo}>
            {({ geographies }) =>
              geographies.map(geo => {
                const name = geo.properties?.name;
                const match = stateColorMap[name];
                const fill = match ? match.color : '#E5E7EB';
                const title = match
                  ? `${name} · ${match.cluster} · Best: ${match.bestGenotype}`
                  : name;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke="#FFFFFF"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', fill: match ? match.color : '#CBD5E0', opacity: 0.85, cursor: 'pointer' },
                      pressed: { outline: 'none' },
                    }}
                  >
                    <title>{title}</title>
                  </Geography>
                );
              })
            }
          </Geographies>
        </ComposableMap>
      ) : (
        <p className="text-xs text-gray-500">Map data unavailable.</p>
      )}
      <div className="flex flex-wrap gap-3 mt-3 text-xs">
        {clusters.map((c, i) => (
          <div key={c.label} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded" style={{ background: CLUSTER_COLORS[i % CLUSTER_COLORS.length] }} />
            <span><b>{c.label}</b> · Best: <b>{c.best_genotype}</b></span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-gray-500 mt-2">
        Hover each state for cluster + best genotype. States are coloured only when the environment name matches an Indian state or a known city → state alias.
      </p>
    </div>
  );
}
